import { useEffect, useState } from 'react';
import { ArrowUpRight, Plug, X } from 'lucide-react';
import { useCompany } from '../context';
import { connectionApi } from '@/workspace/connection-api';
import { useWorkspace } from '@/workspace/context';
import { readSetup, saveSetup, setupKey } from './preferences';
import { ConnectionLogo } from '@/workspace/connection-logo';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import companySettingsCopy from '@/i18n/ns/company-settings';

export function SetupNudge() {
  const { data, ready } = useCompany();
  const ws = useWorkspace();
  if (
    !ready ||
    !data?.membership ||
    !data.company ||
    ws.view !== 'chat' ||
    ws.turnContext.intent.mode !== 'chat'
  )
    return null;
  const key = setupKey(data.company.id, data.membership.userId);
  return (
    <ScopedSetupNudge key={key} storageKey={key} onConnect={() => ws.navigate('connections')} />
  );
}
function ScopedSetupNudge({
  storageKey,
  onConnect,
}: {
  storageKey: string;
  onConnect: () => void;
}) {
  const [dismissed, setDismissed] = useState(() => readSetup(storageKey).dismissed);
  const copy = useNamespaceCopy(companySettingsCopy);
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    if (dismissed) return;
    let active = true;
    void connectionApi
      .list()
      .then((value) => {
        const connected = value.items.filter(
          (item) =>
            item.status === 'connected' ||
            (item.readAccess?.provider === 'postgres' && item.readAccess.access.enabled),
        ).length;
        if (active && (connected > 0 || value.setup?.available)) setCount(connected);
      })
      .catch(() => {}); // An unavailable read is not zero connections.
    return () => {
      active = false;
    };
  }, [dismissed]);
  if (dismissed || count === null) return null;
  return (
    <aside className="setup-nudge" aria-label={copy.setupAria}>
      <Plug size={20} strokeWidth={1.5} aria-hidden="true" />
      <div>
        <strong>{count ? copy.setupMoreTitle : copy.setupFirstTitle}</strong>
        <p>{count ? formatCopy(copy.setupConnected, { count }) : copy.setupFirstBody}</p>
        <button onClick={onConnect}>
          {count ? copy.setupAdd : copy.setupFirst}
          <ArrowUpRight size={14} aria-hidden="true" />
        </button>
      </div>
      <span className="ws-home-apps" aria-label={copy.setupExplore}>
        <ConnectionLogo id="google-workspace" name="Google Workspace" />
        <ConnectionLogo id="slack" name="Slack" />
        <ConnectionLogo id="shopify" name="Shopify" />
        <ConnectionLogo id="github" name="GitHub" />
      </span>
      <button
        className="setup-nudge-dismiss"
        aria-label={copy.setupDismiss}
        onClick={() => {
          saveSetup(storageKey, { ...readSetup(storageKey), dismissed: true });
          setDismissed(true);
        }}
      >
        <X size={15} aria-hidden="true" />
      </button>
    </aside>
  );
}
